import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug = 'dianying' } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab),
    [activeTab]
  );

  const filteredVideos = useMemo(
    () => videos.filter((v) => v.category === activeTab),
    [activeTab]
  );

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory?.name || '' },
  ];

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `好色tv - ${currentCategory?.name || ''}`,
    description: currentCategory?.description || '',
    url: `https://www.haosetv.com/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '好色tv',
      url: 'https://www.haosetv.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6 md:mb-8">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {currentCategory?.name}
            </h1>
            <p className="text-sm text-foreground-500">
              {currentCategory?.description}
            </p>
          </div>

          <div className="flex items-center gap-1.5 mb-6 md:mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleTabChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {filteredVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2.5">
                  <img
                    src={video.image}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 flex-wrap">
                    {video.isVip && (
                      <span className="px-2 py-0.5 bg-accent-500 text-background-950 text-xs font-semibold rounded">
                        VIP
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-background-950/70 text-foreground-950 text-xs rounded">
                      {video.rating}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <span className="text-xs text-white/80">{video.duration}</span>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {video.type} · {video.year}
                </p>
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}